// My-account page (admin/seller/supply) — opened from the bottom-left AccountMenu.
// Profile tab: edit own display_name + language (PATCH /auth/me). Security tab:
// reuses the existing change-password + sessions surface (maket/real aware).
// Admin and seller also get a My Permissions tab (admin: moved out of Settings,
// 2026-10-06; seller: moved out of the sidebar, 2026-10-12 — it shows the
// caller's own permissions, a personal view). `?tab=permissions` opens it
// directly; old Settings links land there.

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { UserCircle, ShieldCheck, KeyRound } from 'lucide-react'
import { USE_REAL_API } from '../../lib/api'
import { fetchMe } from '../../lib/auth'
import { updateProfile } from '../../api/account'
import { useLocaleStore, type Locale } from '../../store/locale'
import { useT } from '../../i18n'
import { ACCOUNT_STRINGS } from '../../i18n/shared/account'
import { Security } from '../settings/Security'
import type { SettingsSurface } from '../settings/Settings'
import { MyPermissionsPage } from '../shared/MyPermissionsPage'
import { profileLocale } from './profileLocale'

function ProfileTab() {
  const t = useT(ACCOUNT_STRINGS)
  const qc = useQueryClient()
  const setLocale = useLocaleStore((s) => s.setLocale)
  const meQ = useQuery({ queryKey: ['auth', 'me'], queryFn: fetchMe, enabled: USE_REAL_API })

  // Draft-overlay over the server value — no setState-in-effect. A null draft
  // means "use the server value"; editing sets the draft; a save clears it so
  // the refetched value takes over again.
  const [draftName, setDraftName] = useState<string | null>(null)
  const [draftLocale, setDraftLocale] = useState<Locale | null>(null)
  const [saved, setSaved] = useState(false)

  const serverName = meQ.data?.user.display_name ?? ''
  const uiLocale = useLocaleStore((s) => s.locale)
  const serverLocale = profileLocale(meQ.data?.user.locale, uiLocale)
  const displayName = draftName ?? serverName
  const locale = draftLocale ?? serverLocale

  const save = useMutation({
    mutationFn: () => updateProfile({ display_name: displayName, locale }),
    onSuccess: (row) => {
      setSaved(true)
      setDraftName(null)
      setDraftLocale(null)
      if (row.locale === 'en' || row.locale === 'tr') setLocale(row.locale)
      void qc.invalidateQueries({ queryKey: ['auth', 'me'] })
    },
  })

  if (!USE_REAL_API) {
    return <p className="text-sm text-slate-500 appearance-dark:text-[var(--color-text-muted)]">{t.maketNote}</p>
  }

  return (
    <div className="max-w-md space-y-4">
      <label className="block text-sm">
        <span className="mb-1 block text-slate-600 appearance-dark:text-[var(--color-text-muted)]">{t.email}</span>
        <input
          value={meQ.data?.user.email ?? ''}
          disabled
          className="w-full rounded-md border border-slate-200 appearance-dark:border-[var(--color-border)] bg-slate-50 appearance-dark:bg-[var(--color-surface-muted)] px-3 py-1.5 text-slate-500 appearance-dark:text-[var(--color-text-muted)]"
        />
      </label>
      <label className="block text-sm">
        <span className="mb-1 block text-slate-600 appearance-dark:text-[var(--color-text-muted)]">{t.displayName}</span>
        <input
          value={displayName}
          onChange={(e) => {
            setDraftName(e.target.value)
            setSaved(false)
          }}
          className="w-full rounded-md border border-slate-300 appearance-dark:border-[var(--color-border)] px-3 py-1.5"
        />
      </label>
      <label className="block text-sm">
        <span className="mb-1 block text-slate-600 appearance-dark:text-[var(--color-text-muted)]">{t.language}</span>
        <select
          value={locale}
          onChange={(e) => {
            setDraftLocale(e.target.value as Locale)
            setSaved(false)
          }}
          className="rounded-md border border-slate-300 appearance-dark:border-[var(--color-border)] px-3 py-1.5"
        >
          <option value="en">English</option>
          <option value="tr">Türkçe</option>
        </select>
      </label>
      <div className="flex items-center gap-3">
        <button
          onClick={() => save.mutate()}
          disabled={save.isPending || !displayName.trim()}
          className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {t.save}
        </button>
        {saved && <span className="text-sm text-emerald-600">{t.saved}</span>}
        {save.isError && (
          <span className="text-sm text-rose-600">{(save.error as Error).message}</span>
        )}
      </div>
    </div>
  )
}

type AccountTab = 'profile' | 'security' | 'permissions'

export function AccountPage({ shell }: { shell: SettingsSurface }) {
  const t = useT(ACCOUNT_STRINGS)
  const showPermissions = shell === 'admin' || shell === 'seller'
  const [tab, setTab] = useState<AccountTab>(() => {
    const requested = new URLSearchParams(window.location.search).get('tab')
    return requested === 'permissions' && showPermissions ? 'permissions' : 'profile'
  })

  const tabBtn = (key: AccountTab, label: string, Icon: typeof UserCircle) => (
    <button
      type="button"
      onClick={() => setTab(key)}
      className={
        'flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium transition-colors ' +
        (tab === key
          ? 'border-slate-900 appearance-dark:border-[var(--color-border)] text-slate-900 appearance-dark:text-[var(--color-text)]'
          : 'border-transparent text-slate-500 appearance-dark:text-[var(--color-text-muted)] hover:text-slate-700 appearance-dark:hover:text-[var(--color-text)]')
      }
    >
      <Icon className="h-4 w-4" /> {label}
    </button>
  )

  return (
    <div className="mx-auto max-w-3xl p-6">
      <h1 className="mb-4 text-2xl font-semibold">{t.pageTitle}</h1>
      <div className="mb-6 flex gap-2 border-b border-slate-200 appearance-dark:border-[var(--color-border)]">
        {tabBtn('profile', t.profileTab, UserCircle)}
        {tabBtn('security', t.securityTab, ShieldCheck)}
        {showPermissions && tabBtn('permissions', t.permissionsTab, KeyRound)}
      </div>
      {tab === 'profile' && <ProfileTab />}
      {tab === 'security' && <Security surface={shell} />}
      {tab === 'permissions' && showPermissions && <MyPermissionsPage role={shell === 'seller' ? 'seller' : 'admin'} />}
    </div>
  )
}
